/**
 * VaultProvider — the browser-side identity vault (client-side pseudonymization).
 *
 * Design: docs/compliance/PSEUDONYMOUS_IDENTITY_PILOT.md
 *
 * Holds the unlocked master key IN MEMORY ONLY (a non-extractable CryptoKey in
 * a ref — never state, never storage). Closing the tab locks the vault.
 * Decrypted identity records live in React state while unlocked so that
 * <PatientName> re-renders when the vault opens.
 *
 * Graceful degradation is a hard requirement: with the feature off, no
 * enrollment, or a locked vault, everything still works — screens simply show
 * the pseudonym (PSN-…) instead of a name.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useAppSelector } from '../store';
import { useFeatureFlags } from '../hooks/useFeatureEnabled';
import {
  enroll as apiEnroll,
  getActiveMemberKeys,
  getMyKeys,
  getVaultStatus,
  initializeVault,
  listVaultItems,
  putVaultItem,
  rotateMasterKey as rotateMasterKeyApi,
} from '../services/identityVaultService';
import {
  decryptRecord,
  encryptRecord,
  generateKeypair,
  generateMasterKeyBytes,
  generatePseudonym,
  generateRecoveryCode,
  importMasterKey,
  importPrivateKey,
  masterKeyFingerprint,
  sealWithPassphrase,
  unsealWithPassphrase,
  unwrapMasterKeyWithPrivateKey,
  wrapMasterKeyForPublicKey,
} from '../services/vaultCrypto';
import type {
  IdentityRecord,
  PatientIdentityMode,
  VaultStatus,
  VaultUiState,
} from '../types/identityVault';

interface VaultContextValue {
  state: VaultUiState;
  status: VaultStatus | null;
  identityMode: PatientIdentityMode;
  /** True when this hospital requires pseudonymous registration. */
  pseudonymousMode: boolean;
  /** Decrypted records by pseudonym (empty unless unlocked). */
  records: Record<string, IdentityRecord>;
  /** Current-version records failed to decrypt after unlock — possible key
   *  substitution or store corruption. Surface this; never hide it. */
  integrityAlert: boolean;
  /** Trust-on-first-use pinning saw the master key change at the same version:
   *  the server handed a different key than the one pinned on this device.
   *  Unlock is refused and writes are blocked until resolved. */
  keyChangedAlert: boolean;
  refresh: () => Promise<void>;
  /** Phase A: generate keypair, seal under the passphrase, upload. */
  enrollWithPassphrase: (passphrase: string) => Promise<void>;
  /** One-time vault creation (admin). Returns the printed recovery code. */
  initialize: () => Promise<string>;
  /** Unseal private key -> unwrap master key -> decrypt all records. */
  unlock: (passphrase: string) => Promise<void>;
  lock: () => void;
  /** Encrypt + store a new identity record; returns its pseudonym. */
  addIdentity: (record: Omit<IdentityRecord, 'pseudonym'>) => Promise<string>;
  /** Update an existing identity with an optimistic-concurrency check; returns
   *  the new version. Throws Error('vault-conflict') if another user edited it
   *  first (local copy is refreshed to the server's current value). */
  updateIdentity: (pseudonym: string, record: Omit<IdentityRecord, 'pseudonym'>) => Promise<number>;
  /** Wrap the in-memory master key to a target user's public key (the Phase-2
   *  grant step; requires the vault unlocked). Returns the wrapped blob. */
  wrapForMember: (publicKeySpkiB64: string) => Promise<string>;
  /** Rotate the hospital master key: re-encrypt every record + re-wrap to
   *  retained members (revokeUserIds lose access). Requires the vault unlocked
   *  and all records decryptable. Returns a NEW printed recovery code. */
  rotateMasterKey: (revokeUserIds?: number[]) => Promise<string>;
  resolve: (pseudonym: string | null | undefined) => IdentityRecord | undefined;
  /** Vault-side name search: name/real-MRN query -> matching records. */
  search: (query: string) => IdentityRecord[];
  /** Encrypted backup file (all ciphertext — safe to store anywhere). */
  exportEncryptedFile: () => Promise<void>;
  /** Break-glass PLAINTEXT CSV (Mock ID / MRN / name …). Unlocked only. */
  exportBreakGlassCsv: () => void;
}

const VaultContext = createContext<VaultContextValue | null>(null);

/** CSV formula-injection guard (playbook §12) + quoting. */
const csvCell = (value: string | undefined): string => {
  let v = value ?? '';
  if (/^[=+\-@]/.test(v)) v = `'${v}`;
  return `"${v.replace(/"/g, '""')}"`;
};

const download = (filename: string, content: string, type: string) => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export const VaultProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // No user (public pages / mid-logout) -> no API calls, and any decrypted
  // identity state is discarded immediately (mirrors the registry's PHI rule).
  const currentUserId = useAppSelector((s) => s.auth.user?.id ?? null);
  // Only probe the vault API when the feature is actually enabled for this
  // user/hospital. The status endpoint is permission-gated (IsIdentityVaultEnabled)
  // and returns 403 by design when the feature is off — probing it unconditionally
  // spammed a console error on every page for hospitals without the vault.
  const { hasFeature, isLoading: featuresLoading } = useFeatureFlags();
  const identityVaultEnabled = hasFeature('identity_vault');
  const [status, setStatus] = useState<VaultStatus | null>(null);
  const [state, setState] = useState<VaultUiState>('loading');
  const [records, setRecords] = useState<Record<string, IdentityRecord>>({});
  // True when current-version records failed to decrypt after a successful
  // unlock — the signal of a substituted master key or corrupted store, which
  // must NOT be silently hidden behind pseudonyms.
  const [integrityAlert, setIntegrityAlert] = useState(false);
  // True when trust-on-first-use pinning saw the master key's fingerprint change
  // at the SAME key version — i.e. the server handed a different key than the one
  // pinned on this device. A hard stop: keys are held but the vault is not
  // considered trusted, and writes are blocked.
  const [keyChangedAlert, setKeyChangedAlert] = useState(false);

  // Key material: refs only — never state, never storage.
  const masterKeyRef = useRef<CryptoKey | null>(null);
  const masterRawRef = useRef<Uint8Array | null>(null); // raw master key; discarded on lock/logout
  const keyVersionRef = useRef<number>(1);
  // Per-record revision counters (pseudonym → version) for optimistic concurrency.
  const versionsRef = useRef<Record<string, number>>({});

  // Best-effort wipe of the raw key bytes before releasing the reference. JS
  // can't guarantee zeroization, but overwriting beats leaving copies for GC.
  const wipeKeys = () => {
    try {
      masterRawRef.current?.fill(0);
    } catch {
      /* ignore */
    }
    masterKeyRef.current = null;
    masterRawRef.current = null;
  };

  // ---- Trust-on-first-use (TOFU) master-key pinning ------------------------
  // Only a NON-SECRET fingerprint (SHA-256 digest) is stored, keyed per hospital.
  // No key material ever touches storage.
  const pinStorageKey = (hospitalId: number) => `aimepic_vault_mkfp_${hospitalId}`;

  const readPin = (hospitalId: number): { version: number; fp: string } | null => {
    try {
      const raw = window.localStorage.getItem(pinStorageKey(hospitalId));
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  const writePin = (hospitalId: number, version: number, fp: string) => {
    try {
      window.localStorage.setItem(pinStorageKey(hospitalId), JSON.stringify({ version, fp }));
    } catch {
      /* private mode / storage full — degrade to no pin */
    }
  };

  /** Verify the just-unwrapped master key against the device's pinned
   *  fingerprint. Returns true if trusted (matches, first use, or a legitimate
   *  higher-version rotation); false if a same-version fingerprint change or a
   *  version downgrade is detected (possible key substitution). */
  const verifyOrPinMasterKey = async (
    hospitalId: number,
    masterRaw: Uint8Array,
    version: number,
  ): Promise<boolean> => {
    const fp = await masterKeyFingerprint(masterRaw);
    const pinned = readPin(hospitalId);
    if (!pinned) {
      writePin(hospitalId, version, fp); // trust on first use
      return true;
    }
    if (version > pinned.version) {
      writePin(hospitalId, version, fp); // legitimate master-key rotation
      return true;
    }
    if (version < pinned.version) return false; // server serving an older key
    return fp === pinned.fp; // same version → fingerprint MUST match
  };

  const deriveState = useCallback((s: VaultStatus | null, unlocked: boolean): VaultUiState => {
    if (!s) return 'unavailable';
    if (unlocked) return 'unlocked';
    if (!s.membership.enrolled) return 'not_enrolled';
    if (s.membership.has_access) return 'locked';
    if (s.is_admin && !s.vault_initialized) return 'can_initialize';
    return 'pending_grant';
  }, []);

  const refresh = useCallback(async () => {
    if (currentUserId == null) {
      setStatus(null);
      setState('unavailable');
      return;
    }
    // Wait until feature flags are known before deciding whether to probe.
    if (featuresLoading) return;
    // Feature off for this user/hospital -> the vault doesn't exist for this
    // session. Skip the API call entirely (it would 403) and degrade gracefully.
    if (!identityVaultEnabled) {
      setStatus(null);
      setState('unavailable');
      return;
    }
    try {
      const s = await getVaultStatus();
      setStatus(s);
      setState(deriveState(s, masterKeyRef.current !== null));
    } catch {
      // No hospital context, or a transient failure — the vault simply doesn't
      // exist for this session.
      setStatus(null);
      setState('unavailable');
    }
  }, [currentUserId, deriveState, featuresLoading, identityVaultEnabled]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Logout wipes every decrypted identity and the in-memory keys.
  useEffect(() => {
    if (currentUserId == null) {
      wipeKeys();
      setRecords({});
      setIntegrityAlert(false);
      setKeyChangedAlert(false);
    }
  }, [currentUserId]);

  const enrollWithPassphrase = useCallback(
    async (passphrase: string) => {
      const pair = await generateKeypair();
      const sealed = await sealWithPassphrase(pair.privateKeyPkcs8, passphrase);
      await apiEnroll({ public_key: pair.publicKeySpkiB64, sealed_private_key: sealed });
      await refresh();
    },
    [refresh],
  );

  const initialize = useCallback(async (): Promise<string> => {
    // The one-safe moment: generate the hospital master key, wrap it to our
    // own public padlock, and seal a recovery copy under a printed code.
    const keys = await getMyKeys();
    if (!keys.public_key) throw new Error('Enroll (set a vault passphrase) first.');
    const masterRaw = generateMasterKeyBytes();
    const wrapped = await wrapMasterKeyForPublicKey(masterRaw, keys.public_key);
    const recoveryCode = generateRecoveryCode();
    const recoverySealed = await sealWithPassphrase(masterRaw, recoveryCode);
    await initializeVault({ wrapped_master_key: wrapped, recovery_sealed_master_key: recoverySealed });
    masterKeyRef.current = await importMasterKey(masterRaw);
    masterRawRef.current = masterRaw;
    // Creating the vault IS the trusted first use — pin this key's fingerprint.
    if (status?.hospital_id != null) {
      await verifyOrPinMasterKey(status.hospital_id, masterRaw, status.master_key_version || 1);
    }
    setRecords({});
    await refresh();
    setState('unlocked');
    return recoveryCode;
  }, [refresh, status?.hospital_id, status?.master_key_version]);

  const loadAndDecryptItems = useCallback(async (masterKey: CryptoKey, currentKeyVersion: number) => {
    const { items } = await listVaultItems();
    const decrypted: Record<string, IdentityRecord> = {};
    const versions: Record<string, number> = {};
    let anomalousFailures = 0;
    await Promise.all(
      items.map(async (item) => {
        versions[item.pseudonym] = item.version;
        try {
          const record = await decryptRecord<IdentityRecord>(masterKey, item.ciphertext);
          decrypted[item.pseudonym] = { ...record, pseudonym: item.pseudonym };
        } catch {
          // Every stored record should be at the current key version (rotation
          // re-encrypts them all, and the stale-key write guard rejects any
          // sealed under an old key). So ANY record that won't decrypt under the
          // key we just unwrapped is anomalous — a substituted/rotated master
          // key, a stale write, or corruption. Never hide it behind a pseudonym.
          void currentKeyVersion;
          anomalousFailures += 1;
        }
      }),
    );
    if (anomalousFailures > 0) {
      // eslint-disable-next-line no-console
      console.error(
        `Identity vault: ${anomalousFailures} current-version record(s) failed to decrypt — ` +
          'possible key substitution or store corruption.',
      );
    }
    setIntegrityAlert(anomalousFailures > 0);
    versionsRef.current = versions;
    setRecords(decrypted);
  }, []);

  const unlock = useCallback(
    async (passphrase: string) => {
      const keys = await getMyKeys();
      if (!keys.wrapped_master_key) throw new Error('no-access');
      let privateRaw: Uint8Array;
      try {
        privateRaw = await unsealWithPassphrase(keys.sealed_private_key, passphrase);
      } catch {
        throw new Error('wrong-passphrase');
      }
      const privateKey = await importPrivateKey(privateRaw);
      const masterRaw = await unwrapMasterKeyWithPrivateKey(keys.wrapped_master_key, privateKey);
      keyVersionRef.current = keys.key_version || 1;

      // TOFU: verify the unwrapped key against this device's pinned fingerprint
      // BEFORE trusting it. A same-version fingerprint change = a server-handed
      // substitute key; refuse to enter the unlocked state and raise the alarm.
      const hospitalId = status?.hospital_id;
      if (hospitalId != null) {
        const trusted = await verifyOrPinMasterKey(hospitalId, masterRaw, keyVersionRef.current);
        if (!trusted) {
          try {
            masterRaw.fill(0);
          } catch {
            /* ignore */
          }
          setKeyChangedAlert(true);
          setState(deriveState(status, false));
          throw new Error('key-changed');
        }
      }
      setKeyChangedAlert(false);

      masterKeyRef.current = await importMasterKey(masterRaw);
      masterRawRef.current = masterRaw;
      await loadAndDecryptItems(masterKeyRef.current, keyVersionRef.current);
      setState('unlocked');
    },
    [loadAndDecryptItems, deriveState, status],
  );

  const lock = useCallback(() => {
    wipeKeys();
    setRecords({});
    setIntegrityAlert(false);
    setKeyChangedAlert(false);
    setState(deriveState(status, false));
  }, [deriveState, status]);

  const addIdentity = useCallback(
    async (record: Omit<IdentityRecord, 'pseudonym'>): Promise<string> => {
      const masterKey = masterKeyRef.current;
      if (!masterKey) throw new Error('vault-locked');
      // Never seal a new identity under an untrusted (possibly substituted) key.
      if (keyChangedAlert) throw new Error('vault-key-untrusted');
      const pseudonym = generatePseudonym();
      const full: IdentityRecord = { ...record, pseudonym };
      const ciphertext = await encryptRecord(masterKey, full);
      // Copy-confirm order: the encrypted identity is stored on the vault
      // server FIRST; only then does the caller create the pseudonymous
      // patient row. Identity never exists in zero places.
      try {
        const res = await putVaultItem(pseudonym, ciphertext, undefined, keyVersionRef.current);
        versionsRef.current[pseudonym] = res.version;
      } catch (e: any) {
        if (e?.response?.status === 409 && e?.response?.data?.stale_key) {
          // The master key was rotated out from under us — never persist an item
          // sealed under the old key. Drop the stale key; the user re-unlocks.
          lock();
          throw new Error('vault-stale-key');
        }
        throw e;
      }
      setRecords((prev) => ({ ...prev, [pseudonym]: full }));
      return pseudonym;
    },
    [keyChangedAlert, lock],
  );

  const updateIdentity = useCallback(
    async (pseudonym: string, record: Omit<IdentityRecord, 'pseudonym'>): Promise<number> => {
      const masterKey = masterKeyRef.current;
      if (!masterKey) throw new Error('vault-locked');
      if (keyChangedAlert) throw new Error('vault-key-untrusted');
      const key = pseudonym.trim().toUpperCase();
      const full: IdentityRecord = { ...record, pseudonym: key };
      const ciphertext = await encryptRecord(masterKey, full);
      const expected = versionsRef.current[key];
      try {
        // Optimistic concurrency: the server accepts the write only if the row
        // is still at ``expected``; otherwise it 409s with the current state.
        const res = await putVaultItem(key, ciphertext, expected, keyVersionRef.current);
        versionsRef.current[key] = res.version;
        setRecords((prev) => ({ ...prev, [key]: full }));
        return res.version;
      } catch (e: any) {
        if (e?.response?.status === 409 && e?.response?.data?.stale_key) {
          // Master key rotated mid-edit — never write under the old key.
          lock();
          throw new Error('vault-stale-key');
        }
        if (e?.response?.status === 409) {
          // Someone else edited this record first. Adopt the server's current
          // version + decrypted value and signal the caller to redo the edit.
          const data = e.response.data || {};
          if (typeof data.current_version === 'number') versionsRef.current[key] = data.current_version;
          if (data.ciphertext) {
            try {
              const serverRecord = await decryptRecord<IdentityRecord>(masterKey, data.ciphertext);
              setRecords((prev) => ({ ...prev, [key]: { ...serverRecord, pseudonym: key } }));
            } catch {
              /* leave the local copy */
            }
          }
          throw new Error('vault-conflict');
        }
        throw e;
      }
    },
    [keyChangedAlert, lock],
  );

  const wrapForMember = useCallback(
    async (publicKeySpkiB64: string): Promise<string> => {
      const masterRaw = masterRawRef.current;
      if (!masterRaw) throw new Error('vault-locked');
      if (keyChangedAlert) throw new Error('vault-key-untrusted');
      return wrapMasterKeyForPublicKey(masterRaw, publicKeySpkiB64);
    },
    [keyChangedAlert],
  );

  const rotateMasterKey = useCallback(
    async (revokeUserIds: number[] = []): Promise<string> => {
      const hospitalId = status?.hospital_id;
      if (hospitalId == null) throw new Error('vault-unavailable');
      if (masterKeyRef.current === null) throw new Error('vault-locked');
      if (keyChangedAlert) throw new Error('vault-key-untrusted');
      // Re-encrypting requires every record to be readable; refuse if some
      // current-version records failed to decrypt.
      if (integrityAlert) throw new Error('vault-integrity');
      const revoke = new Set(revokeUserIds);
      if (currentUserId != null && revoke.has(currentUserId)) throw new Error('cannot-revoke-self');

      const newMasterRaw = generateMasterKeyBytes();
      const newMasterKey = await importMasterKey(newMasterRaw);

      // 1) re-encrypt every identity record under the new key
      const items: { pseudonym: string; ciphertext: string }[] = [];
      for (const [pseudonym, record] of Object.entries(records)) {
        items.push({ pseudonym, ciphertext: await encryptRecord(newMasterKey, record) });
      }

      // 2) re-wrap the new key to each RETAINED active member
      const { members: active } = await getActiveMemberKeys(hospitalId);
      const members: { user_id: number; wrapped_master_key: string }[] = [];
      for (const m of active) {
        if (revoke.has(m.user_id)) continue;
        members.push({
          user_id: m.user_id,
          wrapped_master_key: await wrapMasterKeyForPublicKey(newMasterRaw, m.public_key),
        });
      }
      if (currentUserId != null && !members.some((m) => m.user_id === currentUserId)) {
        // Should never happen: the rotating admin holds a key and isn't revoked.
        throw new Error('rotate-self-missing');
      }

      const newVersion = (status?.master_key_version || 1) + 1;
      const recoveryCode = generateRecoveryCode();
      const recoverySealed = await sealWithPassphrase(newMasterRaw, recoveryCode);

      await rotateMasterKeyApi(
        {
          new_master_key_version: newVersion,
          members,
          items,
          recovery_sealed_master_key: recoverySealed,
        },
        hospitalId,
      );

      // The server has now swapped everything to the new key. From here the OLD
      // in-memory key is stale and MUST NOT be used for any further write. If
      // adopting the new key fails, lock rather than continuing with the old one.
      try {
        // 3) adopt the new key locally + re-pin (higher version → legit rotation)
        wipeKeys();
        masterKeyRef.current = newMasterKey;
        masterRawRef.current = newMasterRaw;
        keyVersionRef.current = newVersion;
        await verifyOrPinMasterKey(hospitalId, newMasterRaw, newVersion);
        await loadAndDecryptItems(newMasterKey, newVersion);
        await refresh();
      } catch {
        lock();
        throw new Error('vault-rotated-relock');
      }
      return recoveryCode;
    },
    [status, records, currentUserId, keyChangedAlert, integrityAlert, loadAndDecryptItems, refresh, lock],
  );

  const resolve = useCallback(
    (pseudonym: string | null | undefined) => (pseudonym ? records[pseudonym.trim().toUpperCase()] : undefined),
    [records],
  );

  const search = useCallback(
    (query: string): IdentityRecord[] => {
      const q = query.trim().toLowerCase();
      if (!q) return [];
      return Object.values(records).filter((r) =>
        [r.first_name, r.middle_name, r.last_name, r.real_mrn, r.national_id, r.phone]
          .filter(Boolean)
          .some((v) => (v as string).toLowerCase().includes(q)),
      );
    },
    [records],
  );

  const exportEncryptedFile = useCallback(async () => {
    // Everything in this file is ciphertext or a public key — safe at rest.
    const keys = await getMyKeys();
    const { items } = await listVaultItems();
    const payload = {
      format: 'aimepic-identity-vault-backup',
      v: 1,
      exported_at: new Date().toISOString(),
      hospital_id: status?.hospital_id,
      public_key: keys.public_key,
      sealed_private_key: keys.sealed_private_key,
      wrapped_master_key: keys.wrapped_master_key,
      items,
    };
    download(
      `identity-vault-backup-${new Date().toISOString().slice(0, 10)}.json`,
      JSON.stringify(payload, null, 2),
      'application/json',
    );
  }, [status?.hospital_id]);

  const exportBreakGlassCsv = useCallback(() => {
    if (masterKeyRef.current === null) throw new Error('vault-locked');
    const header = ['Mock ID', 'Real MRN', 'First name', 'Middle name', 'Last name', 'DOB', 'National ID', 'Phone'];
    const rows = Object.values(records)
      .sort((a, b) => a.pseudonym.localeCompare(b.pseudonym))
      .map((r) =>
        [r.pseudonym, r.real_mrn, r.first_name, r.middle_name, r.last_name, r.date_of_birth, r.national_id, r.phone]
          .map(csvCell)
          .join(','),
      );
    download(
      `identity-vault-BREAK-GLASS-${new Date().toISOString().slice(0, 10)}.csv`,
      [header.map(csvCell).join(','), ...rows].join('\r\n'),
      'text/csv',
    );
  }, [records]);

  const identityMode: PatientIdentityMode = status?.patient_identity_mode ?? 'REAL';

  const value = useMemo<VaultContextValue>(
    () => ({
      state,
      status,
      identityMode,
      pseudonymousMode: identityMode === 'PSEUDONYMOUS',
      records,
      integrityAlert,
      keyChangedAlert,
      refresh,
      enrollWithPassphrase,
      initialize,
      unlock,
      lock,
      addIdentity,
      updateIdentity,
      wrapForMember,
      rotateMasterKey,
      resolve,
      search,
      exportEncryptedFile,
      exportBreakGlassCsv,
    }),
    [
      state,
      status,
      identityMode,
      records,
      integrityAlert,
      keyChangedAlert,
      refresh,
      enrollWithPassphrase,
      initialize,
      unlock,
      lock,
      addIdentity,
      updateIdentity,
      wrapForMember,
      rotateMasterKey,
      resolve,
      search,
      exportEncryptedFile,
      exportBreakGlassCsv,
    ],
  );

  return <VaultContext.Provider value={value}>{children}</VaultContext.Provider>;
};

// Stable no-op vault for components rendered outside the provider, so shared
// components (<PatientName>) never crash in unmounted contexts.
const UNAVAILABLE_VAULT: VaultContextValue = {
  state: 'unavailable',
  status: null,
  identityMode: 'REAL',
  pseudonymousMode: false,
  records: {},
  integrityAlert: false,
  keyChangedAlert: false,
  refresh: async () => undefined,
  enrollWithPassphrase: async () => {
    throw new Error('vault-unavailable');
  },
  initialize: async () => {
    throw new Error('vault-unavailable');
  },
  unlock: async () => {
    throw new Error('vault-unavailable');
  },
  lock: () => undefined,
  addIdentity: async () => {
    throw new Error('vault-unavailable');
  },
  updateIdentity: async () => {
    throw new Error('vault-unavailable');
  },
  wrapForMember: async () => {
    throw new Error('vault-unavailable');
  },
  rotateMasterKey: async () => {
    throw new Error('vault-unavailable');
  },
  resolve: () => undefined,
  search: () => [],
  exportEncryptedFile: async () => undefined,
  exportBreakGlassCsv: () => undefined,
};

export const useVault = (): VaultContextValue => {
  const ctx = useContext(VaultContext);
  return ctx ?? UNAVAILABLE_VAULT;
};

export default VaultContext;
